import Box from "@mui/material/Box";
import Collapse from "@mui/material/Collapse";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import ListItemButton from "@mui/material/ListItemButton";
import ListItemText from "@mui/material/ListItemText";
import { usePreloadDmsNode } from "features/DMS/hooks/usePreloadDmsNode";
import { NotificationDot } from "features/Notifications/NotificationDot";
import type { HTMLAttributeAnchorTarget, MouseEventHandler } from "react";
import { useTranslation } from "react-i18next";
import { CustomLink } from "../CustomLink";
import { ExpandButton } from "./ExpandButton";
import { MobileNavCategoryMenuItem } from "./MobileNavCategoryMenuItem";
import type { CategoryMenuItem, MenuLibraryItem } from "./MobileNavDefs";

const TEST_ID = "mobile-nav-root-menu-item";

interface MobileNavLibraryListItemProps {
  id: string;
  displayName: string;
  path: string;
}
function MobileNavLibraryListItem({
  displayName,
  path,
  id,
}: MobileNavLibraryListItemProps) {
  const preloadNode = usePreloadDmsNode();
  return (
    <ListItem disablePadding key={id}>
      <ListItemButton
        sx={{ pl: 6 }}
        component={CustomLink}
        onPointerOver={() => {
          preloadNode(path!, "Library", "view");
        }}
        href={`/dms/library/v2${path}`}
      >
        <ListItemText
          primary={displayName}
          slotProps={{ primary: { color: "primary.main" } }}
        />
      </ListItemButton>
    </ListItem>
  );
}

interface MobileNavRootMenuItemProps {
  id: string;
  open: boolean;
  url?: string | undefined;
  target?: HTMLAttributeAnchorTarget | undefined;
  displayName: string;
  categories: CategoryMenuItem[];
  libraries: MenuLibraryItem[];
  onExpandClick: MouseEventHandler<HTMLButtonElement>;
}

export function MobileNavRootMenuItem({
  id,
  open,
  url,
  target,
  displayName,
  categories,
  libraries,
  onExpandClick,
}: MobileNavRootMenuItemProps) {
  const { t } = useTranslation("Fixhub", {
    keyPrefix: "Next:Core:MobileNavRootMenuItem",
  });
  const hasChildren = libraries.length > 0 || categories.length > 0;
  const LibrariesList = libraries.length > 0 && (
    <List disablePadding dense>
      <ListItem sx={{ pl: 4 }}>
        <ListItemText
          primary={t("library")}
          slotProps={{ primary: { fontWeight: "bold" } }}
        />
      </ListItem>
      {libraries.map((library) => (
        <MobileNavLibraryListItem key={library.id} {...library} />
      ))}
    </List>
  );

  if (!hasChildren && !url) {
    return (
      <ListItem data-testid={TEST_ID}>
        <ListItemText
          primary={displayName}
          slotProps={{ primary: { fontWeight: "bold" } }}
        />
      </ListItem>
    );
  }

  if (!hasChildren && url) {
    return (
      <ListItem data-testid={TEST_ID} disablePadding>
        <ListItemButton component={CustomLink} href={url} target={target}>
          <ListItemText
            primary={
              <>
                {displayName}
                <NotificationDot id={id} mode="self" />
              </>
            }
            slotProps={{
              primary: {
                fontWeight: "bold",
                color: "primary.main",
              },
            }}
          />
        </ListItemButton>
      </ListItem>
    );
  }

  if (!url) {
    return (
      <>
        <ListItem
          data-testid={TEST_ID}
          secondaryAction={
            <ExpandButton
              data-testid="expand-button"
              open={open}
              onClick={onExpandClick}
            />
          }
        >
          <ListItemText
            primary={
              <>
                {displayName} <NotificationDot id={id} mode="either" />
              </>
            }
            slotProps={{ primary: { fontWeight: "bold" } }}
          />
        </ListItem>

        <Collapse in={open}>
          <Box>
            {LibrariesList}
            {categories.map((props) => (
              <MobileNavCategoryMenuItem key={props.id} {...props} />
            ))}
          </Box>
        </Collapse>
      </>
    );
  }

  return (
    <>
      <ListItem
        data-testid={TEST_ID}
        disablePadding
        secondaryAction={
          <ExpandButton
            data-testid="expand-button"
            open={open}
            onClick={onExpandClick}
          />
        }
      >
        <ListItemButton component={CustomLink} href={url} target={target}>
          <ListItemText
            slotProps={{
              primary: {
                color: "primary.main",
                fontWeight: "bold",
              },
            }}
            primary={
              <>
                {displayName}
                <NotificationDot id={id} mode="self" />
              </>
            }
          />
        </ListItemButton>
      </ListItem>
      <Collapse in={open}>
        <Box>
          {LibrariesList}
          {categories.map((props) => (
            <MobileNavCategoryMenuItem key={props.id} {...props} />
          ))}
        </Box>
      </Collapse>
    </>
  );
}
